import * as React from 'react';
import downloadFile from 'downloadjs';
import { connect } from 'react-redux';
import { get, isEqual } from 'lodash';

import Box from 'components/box/Box';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';

import SentimentAnalysisTable from './sentiment-analysis-table/SentimentAnalysisTable';
import SentimentAnalysisFilters from './sentiment-analysis-filters/SentimentAnalysisFilters';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { changeSentimentClass, fetchSentimentAnalysis, resetSentimentAnalysis } from '../../reducers/admin-sentiment-analysis/admin-sentiment-analysis';

import { API, get as getApi } from 'utils/api';
import { snakeCaseKeys } from 'utils/keys';
import { Querystring } from 'utils/url';
import { logError } from 'utils/log';
import { updateQueryParams } from 'utils/location';

import type {
  AdminSentimentAnalysisQuery,
  AdminSentimentAnalysisQueryXlsx,
  LocationWindow,
  Pagination as PaginationType,
  SentimentAnalysisItem,
  SentimentClass
} from 'types';

type StateProps = {
  items: Array<SentimentAnalysisItem>,
  isLoading: boolean,
  pagination: PaginationType,
  location: LocationWindow
};

type DispatchProps = {
  dispatchFetchSentimentAnalysisResults: (query: AdminSentimentAnalysisQuery, silent: boolean) => Promise<void>,
  dispatchResetSentimentAnalysisResults: () => void,
  dispatchChangeSentimentClass: (deliverableId: number, commentUuid: string, sentimentClass: SentimentClass) => Promise<void>,
  dispatchShowLoading: (url: string) => void,
  dispatchHideLoading: (url: string) => void
};

type OwnProps = {
  campaignId: number
};

type Props = OwnProps & StateProps & DispatchProps;

class SentimentAnalysis extends React.Component<Props> {
  componentDidMount() {
    this.fetchSentimentAnalysisResults(this.props.location.query || {}, false);
  }

  componentDidUpdate(prevProps: Props) {
    const { query } = this.props.location;

    if (!isEqual(prevProps.location.query, query)) {
      this.fetchSentimentAnalysisResults(query || {}, false);

      window.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    const { dispatchResetSentimentAnalysisResults } = this.props;

    dispatchResetSentimentAnalysisResults();
  }

  getDeliverableId(): number | undefined | null {
    const { location } = this.props;

    return get(location, 'query.deliverableId') || null;
  }

  getCreatorId(): number | undefined | null {
    const { location } = this.props;

    return get(location, 'query.influencerId') || null;
  }

  fetchSentimentAnalysisResults(urlQuery: {
    [x: string]: any
  }, silent: boolean) {
    const { campaignId, dispatchFetchSentimentAnalysisResults, dispatchResetSentimentAnalysisResults } = this.props;
    const taskTypes = urlQuery.taskTypes === 'all' ? 'instagram_post, instagram_reel, tiktok_video' : urlQuery.taskTypes;

    const query = {
      campaignId,
      creatorId: this.getCreatorId(),
      deliverableId: this.getDeliverableId(),
      page: urlQuery.page || 1,
      sentimentClass: urlQuery.sentimentClass || '',
      taskTypes,
      sort: urlQuery.sort || 'creator_name.asc'
    };

    if (query.campaignId || query.creatorId || query.deliverableId || query.taskTypes) {
      dispatchFetchSentimentAnalysisResults(query, silent);
    } else {
      dispatchResetSentimentAnalysisResults();
    }
  }

  handleOptionClick = (option: string) => {
    switch (option) {
      case 'download-excel':
        this.downloadSentimentAnalysisXlsx();
        break;
      default:
        break;
    }
  };


  downloadSentimentAnalysisXlsx = () => {
    const { location, dispatchShowLoading, dispatchHideLoading } = this.props;
    const { campaignId } = this.props;

    const {
      query
    }: {
      [x: string]: any
    } = location;
    const {
      creatorId,
      deliverableId,
      sentimentClass,
      taskType
    }: AdminSentimentAnalysisQueryXlsx = {
      creatorId: this.getCreatorId(),
      deliverableId: this.getDeliverableId(),
      sentimentClass: query.sentimentClass || '',
      taskType: query.taskType
    };

    if (!campaignId) {
      return;
    }
    const baseUrl = API.END_POINTS.ADMIN.downloadSentimentAnalysisAsExcel(campaignId);
    let params = snakeCaseKeys({
      filterBy: decodeURIComponent(sentimentClass || ''),
      taskType
    });

    if (campaignId && deliverableId) {
      params = {
        ...params,
        ...snakeCaseKeys({
          deliverableId
        })
      };
    } else if (campaignId && creatorId) {
      params = {
        ...params,
        ...snakeCaseKeys({
          creatorIds: creatorId
        })
      };
    }

    const queryString = Querystring.objectToString(params);
    const url = `${baseUrl}?${queryString}`;
    const asyncHandler = async () => {
      try {
        dispatchShowLoading(url);
        const { data } = await getApi(url, { responseType: 'blob' }, { suppressGlobalNotification: true });
        const fileNameStr = (creatorId || deliverableId) ? ` - ${creatorId || deliverableId || ''}` : '';
        const fileName = `Sentiment Analysis Comments:${campaignId}${fileNameStr}.xlsx`;
        downloadFile(data, fileName);
        dispatchHideLoading(url);
      } catch (error) {
        dispatchHideLoading(url);
        logError(error);
        throw error;
      }
    };
    asyncHandler();
  };

  handlePageChange = (page: number): void => {
    const { location } = this.props;

    updateQueryParams(location, { page });
  };

  handleSentimentClassChange = async (item: SentimentAnalysisItem, sentimentClass: SentimentClass) => {
    const { dispatchChangeSentimentClass, location } = this.props;
    const selectedSentiments = location.query?.sentimentClass?.split(',').filter(Boolean) ?? [];

    await dispatchChangeSentimentClass(item.deliverableId, item.commentUuid, sentimentClass);

    if (selectedSentiments.length > 0 && selectedSentiments.length < 3) {
      this.fetchSentimentAnalysisResults(location.query, true);
    }
  };

  render() {
    const { items, isLoading, pagination, location, campaignId } = this.props;

    return (
      <section>
        <Box
          bordered={false}
          boxed={false}
        >
          <SentimentAnalysisFilters
            campaignId={campaignId}
          />
        </Box>
        {
          isLoading ?
            <Spinner
              position="inline"
              size="small"
              color="light"
            /> :
            <React.Fragment>
              <SentimentAnalysisTable
                location={location}
                items={items}
                itemsCount={Number(pagination.itemsTotal) || 0}
                onChange={this.handleSentimentClassChange}
                onOptionClick={this.handleOptionClick}
              />
              <Pagination
                location={location}
                meta={pagination}
                links={pagination.links}
                onClick={this.handlePageChange}
              />
            </React.Fragment>
        }
      </section>
    );
  }
}

const mapStateToProps = ({ adminSentimentAnalysis, router }): StateProps => ({
  items: adminSentimentAnalysis.list,
  isLoading: adminSentimentAnalysis.isLoading,
  pagination: adminSentimentAnalysis.pagination,
  location: router.location
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSentimentAnalysisResults: (query, silent) => dispatch(fetchSentimentAnalysis(query, silent, true)),
  dispatchResetSentimentAnalysisResults: () => dispatch(resetSentimentAnalysis()),
  dispatchChangeSentimentClass: (deliverableId, commentUuid, sentimentClass) => dispatch(changeSentimentClass(deliverableId, commentUuid, sentimentClass)),
  dispatchShowLoading: (url) => dispatch(showLoading(url)),
  dispatchHideLoading: (url) => dispatch(hideLoading(url))
});

export default connect(mapStateToProps, mapDispatchToProps)(SentimentAnalysis);
